// Lo que el agente propone recordar de un archivo recien subido (5.2, R4, CA3).
//
// **Por que aqui y no en la puerta de Knowledge**, que ya existe y resuelve lo
// mismo: el dueño del producto probo esa pantalla y su conclusion fue que
// aquello deberia ser una conversacion, no un formulario con una lista de
// propuestas que no se entiende (`spec.md:28`). Subir el archivo desde el panel
// y tener que irse a otra pantalla a decir que si es justo el paso que este
// plan quita.
//
// **No duplica la puerta, comparte su camino**: la red es la misma
// -`workspacesService.pendientes()` y `resolverPendiente()`-, y lo unico que
// cambia es como se lee. Aqui no hay lista ni encabezado con recuento, porque
// una conversacion no es un buzon: llega lo de este archivo y nada mas.
//
// **El orden es el de D3** (4.2): delante que se propone recordar, detras que
// pasara si dices que si. Lo aprendido en Knowledge el 2026-09-21 es que con la
// consecuencia de titular cuatro propuestas distintas se leen iguales.
//
// Como `ConfirmacionPendiente`, esto **no protege nada**: la memoria sigue
// esperando en el servidor hasta que alguien la aprueba. Solo pregunta.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalloutCard } from '@/components/ui/callout-card'
import { Input } from '@/components/ui/input'
import type { Pendiente } from '@/services/workspacesService'

/** Lo que se ha decidido de cada propuesta, por su identificador. */
export type ResolvedProposals = Record<string, 'accepted' | 'rejected'>

interface Props {
  proposals: Pendiente[]
  resolved?: ResolvedProposals
  /** El rechazo lleva motivo **porque el servidor lo exige**: sin el se vuelve
   *  a proponer lo mismo mañana sin que nadie recuerde que ya se dijo que no. */
  onResolve: (proposal: Pendiente, decision: 'aprobar' | 'rechazar', reason: string) => void
  busy?: string
}

export function FileProposals({ proposals, resolved = {}, onResolve, busy }: Props) {
  const { t } = useTranslation()
  const [rejecting, setRejecting] = useState('')
  const [reason, setReason] = useState('')

  if (proposals.length === 0) return null

  return (
    <div className="mt-3 space-y-2">
      {proposals.map((proposal) => {
        const decision = resolved[proposal.id]
        const working = busy === proposal.id

        return (
          // La envoltura es la misma que la de la confirmacion, y desde el
          // 2026-09-23 se escribe una sola vez: ver `CalloutCard`.
          <CalloutCard key={proposal.id} icon={FileText}>
            <p className="break-words text-sm font-medium">
              {typeof proposal.propone === 'string' && proposal.propone
                ? proposal.propone
                : proposal.motivo}
            </p>

            {proposal.efecto && (
              <p className="mt-2 text-xs text-muted-foreground">{proposal.efecto}</p>
            )}

            {decision === 'accepted' && (
              <p className="mt-2 text-xs text-success">{t('brain.propuesta.aceptada')}</p>
            )}
            {decision === 'rejected' && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('brain.propuesta.rechazada')}
              </p>
            )}

            {!decision && rejecting !== proposal.id && (
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  disabled={working}
                  onClick={() => onResolve(proposal, 'aprobar', '')}
                >
                  {working ? t('brain.propuesta.guardando') : t('brain.propuesta.aceptar')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={working}
                  onClick={() => setRejecting(proposal.id)}
                >
                  {t('brain.propuesta.rechazar')}
                </Button>
              </div>
            )}

            {!decision && rejecting === proposal.id && (
              <div className="mt-3 space-y-2">
                <Input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t('brain.propuesta.motivo')}
                  aria-label={t('brain.propuesta.motivo')}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={working || reason.trim() === ''}
                    onClick={() => {
                      onResolve(proposal, 'rechazar', reason.trim())
                      setRejecting('')
                      setReason('')
                    }}
                  >
                    {t('brain.propuesta.confirmar_rechazo')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={working}
                    onClick={() => {
                      setRejecting('')
                      setReason('')
                    }}
                  >
                    {t('brain.cancelar')}
                  </Button>
                </div>
              </div>
            )}
          </CalloutCard>
        )
      })}
    </div>
  )
}
